import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";
import { EntityConfig, downloadBlob } from "./types";
import { StreamingServiceCombobox } from "@/components/shared/StreamingServiceCombobox";
import { SupplierCombobox } from "@/components/shared/SupplierCombobox";
import { Supplier } from "@/services/supplier";
import { streamingAccountService, EStreamingAccountStatus } from "@/services/streaming-account";

interface AccountExportPanelProps {
  entity: EntityConfig;
}

export function AccountExportPanel({ entity }: AccountExportPanelProps) {
  const [status, setStatus] = useState<string>("ALL");
  const [serviceId, setServiceId] = useState<string>("all");
  const [supplier, setSupplier] = useState<Supplier | null>(null);

  const exportMutation = useMutation({
    mutationFn: () =>
      streamingAccountService.exportAccounts({
        status: status !== "ALL" ? (status as EStreamingAccountStatus) : undefined,
        serviceId: serviceId !== "all" ? Number(serviceId) : undefined,
        supplierId: supplier ? supplier.id : undefined,
      }),
    onSuccess: ({ blob, fileName }) => {
      downloadBlob(blob, fileName);
      toast.success("Exportación generada correctamente.");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor={`export-status-${entity.id}`}>Estado</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger id={`export-status-${entity.id}`}>
              <SelectValue placeholder="Todos los estados" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todos los estados</SelectItem>
              {entity.statusOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label>Servicio</Label>
          <StreamingServiceCombobox value={serviceId} onChange={setServiceId} />
        </div>

        <div className="space-y-1.5">
          <Label>Proveedor</Label>
          <SupplierCombobox value={supplier} onChange={setSupplier} />
        </div>
      </div>

      <div className="flex justify-end pt-1">
        <Button onClick={() => exportMutation.mutate()} disabled={exportMutation.isPending}>
          {exportMutation.isPending ? (
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          ) : (
            <Download className="w-4 h-4 mr-1.5" />
          )}
          Generar exportación
        </Button>
      </div>
    </div>
  );
}
